import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DialogService } from 'shared/services/dialog.service';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { ClientsService, PaginatedDbClients } from 'shared/services/clients.service';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Utils } from 'fakturnia-shared';
import { ClientAddressPipe } from 'shared/pipes/client-address.pipe';
import { KsefClientValidatorUtils } from 'shared/utils/ksef-client-validator.utils';

@Component({
  standalone: true,
  selector: 'app-select-client-dialog',
  imports: [CommonModule, MatFormFieldModule, MatDialogModule, MatInputModule, MatButtonModule, MatIconModule, MatTooltipModule, ReactiveFormsModule, ClientAddressPipe],
  templateUrl: './select-client-dialog.component.html',
  styleUrl: './select-client-dialog.component.scss'
})
export class SelectClientDialogComponent implements OnDestroy {

  private _subscriptions: Subscription[] = [];

  public searchControl = new FormControl('', { nonNullable: true })
  public options: any = [];

  constructor(
    private _dialogService:DialogService,
    private _clientsService:ClientsService,
    private _dialogRef: MatDialogRef<SelectClientDialogComponent>
    ) {

      // Load default last added companies.
      const pagination:PaginatedDbClients = this._clientsService.browseClients({ currentPage: 1, pageSize: 30, activeFilters: [], filters: [(client) => client.isDeleted == false]})
      this.options = pagination.results

      if(pagination.resultsCount == 0) {
        this.showAddClientDialog()
      }
      
      this._subscriptions.push(this.searchControl.valueChanges.subscribe({
        next: (data) => {
          if(Utils.isNullOrEmpty(data)) {
            const pagination:PaginatedDbClients = this._clientsService.browseClients({ currentPage: 1, pageSize: 30, activeFilters: [], filters: [(client)=> client.isDeleted == false]})
            this.options = pagination.results
            return
          }
          this._filterClients(data)
        }
      }))

  }

  public showAddClientDialog() {
    this._subscriptions.push(
      this._dialogService.showAddClientDialog()
        .subscribe({
          next: (response: any) => {

            if (!response) return
            if (typeof response.success == 'undefined') return
            if (!response.success) return

            this.selectClient(response.data)
          }
    }))
  }

  public selectClient(client) {
    this._dialogRef.close({
      client: client
    })
  }

  public close() {
    this._dialogRef.close()
  }

  /**
   * Zwraca sformatowany identyfikator klienta na podstawie identifierType.
   */
  public getClientIdentifier(client: any): string | null {
    if (!client) return null;

    const identifierType = client.identifierType;

    // Jeśli identifierType jest ustawiony, użyj go
    if (identifierType === 'nip' && client.nip) {
      return 'NIP: ' + client.nip;
    }
    if (identifierType === 'vatEu' && client.vatUE) {
      return 'VAT UE: ' + this._formatVatUE(client.vatUE, client.countryCode);
    }
    if (identifierType === 'other' && client.otherTaxIdentifier) {
      return 'ID: ' + client.otherTaxIdentifier;
    }
    if (identifierType === 'idWew' && client.idWew) {
      return 'IDWew: ' + client.idWew;
    }
    if (identifierType === 'none') {
      return null;
    }

    // Fallback dla starych klientów bez identifierType
    if (client.nip) return 'NIP: ' + client.nip;
    if (client.vatUE) return 'VAT UE: ' + this._formatVatUE(client.vatUE, client.countryCode);
    if (client.otherTaxIdentifier) return 'ID: ' + client.otherTaxIdentifier;

    return null;
  }

  private _formatVatUE(vatUE: string, countryCode?: string): string {
    if (!vatUE) return vatUE;
    if (/^[A-Z]{2}/i.test(vatUE)) return vatUE;
    if (countryCode && countryCode !== 'PL') return countryCode + vatUE;
    return vatUE;
  }

  /**
   * Sprawdza czy kontrahent ma niekompletne dane wymagane do wysylki faktury do KSeF.
   * Weryfikuje: ulice, kod pocztowy, miasto, oraz poprawnosc identyfikatora podatkowego.
   */
  public hasKsefWarning(client: any): boolean {
    return KsefClientValidatorUtils.hasKsefWarning(client);
  }

  /**
   * Zwraca liste ostrzezen dotyczacych danych kontrahenta wymaganych przez KSeF.
   */
  public getKsefWarnings(client: any): string[] {
    return KsefClientValidatorUtils.getKsefWarnings(client);
  }

  private _filterClients(value) {
    this.options = this._clientsService.filterClients(value)
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe())
  }

}
